/**
 * Clip method property getter and registration.
 *
 * Storage key: identifier=`clip_method.{annotationId}`, objectIdentifier=`filters`, type=PROJECT_USER
 *
 * The static config uses `dynamicIdentifier` so PropertyPanel resolves
 * the identifier per-annotation and `objectIdentifierOverride` so it reads
 * from the correct objectIdentifier.
 */

import { AnnotationType, PropertyType, PropertyValueType } from '../../types';
import { propertyRegistry } from '../../PropertyRegistry';
import { ReactiveProperty } from '../../reactive/ReactiveProperty';
import { getGlobalPropertyService, hasGlobalPropertyService } from '../../reactive/globalPropertyService';

const FILTER_OBJECT_ID = 'filters';

/** Annotation types that support filtering */
const FILTERABLE_TYPES = [AnnotationType.BOX, AnnotationType.POINTCLUSTER, AnnotationType.SURFACE];

/**
 * Register the static clip method config for PropertyPanel auto-rendering.
 * HEATMAP option is restricted to PLANE via annotationTypes on the option.
 */
function registerClipMethodConfig(): void {
  if (propertyRegistry.isRegistered('clip_method.{objectIdentifier}')) return;
  propertyRegistry.register({
    type: PropertyType.PROJECT_USER,
    valueType: PropertyValueType.SELECT,
    identifier: 'clip_method.{objectIdentifier}',
    name: 'Clip method',
    annotationTypes: FILTERABLE_TYPES,
    defaultValue: 'ISOLATE',
    dynamicIdentifier: true,
    objectIdentifierOverride: FILTER_OBJECT_ID,
    ui: {
      group: 'filters',
      order: 1,
    },
    validation: {
      options: [
        { value: 'HIGHLIGHT', label: 'highlight' },
        { value: 'HIDE', label: 'hide' },
        { value: 'ISOLATE', label: 'isolate' },
        { value: 'HEATMAP', label: 'heatmap', annotationTypes: [AnnotationType.SURFACE] },
      ],
    },
  });
}

// Auto-register on import
registerClipMethodConfig();

/**
 * Ensure the per-instance reactive property exists in the PropertyService.
 * Called by Potree-side code (Volume, Plane, PointCluster) to subscribe.
 */
export function ensureClipMethodPropertyRegistered(annotationIdentifier: string): string {
  const propertyIdentifier = `clip_method.${annotationIdentifier}`;

  if (!propertyRegistry.isRegistered(propertyIdentifier)) {
    propertyRegistry.register({
      type: PropertyType.PROJECT_USER,
      valueType: PropertyValueType.SELECT,
      identifier: propertyIdentifier,
      name: `clip method for ${annotationIdentifier}`,
      annotationTypes: [],
      defaultValue: 'ISOLATE',
      validation: {
        options: [
          { value: 'HIGHLIGHT', label: 'highlight' },
          { value: 'HIDE', label: 'hide' },
          { value: 'ISOLATE', label: 'isolate' },
          { value: 'HEATMAP', label: 'heatmap' },
        ],
      },
    });
  }

  return propertyIdentifier;
}

/**
 * Get reactive property for a clip method (non-hook version for Potree).
 * Returns null if the global property service is not yet initialized.
 */
export function getClipMethodProperty(annotationIdentifier: string): ReactiveProperty<string> | null {
  if (!hasGlobalPropertyService()) return null;

  const propertyIdentifier = ensureClipMethodPropertyRegistered(annotationIdentifier);
  const service = getGlobalPropertyService();
  return service.getProperty(propertyIdentifier, FILTER_OBJECT_ID, PropertyType.PROJECT_USER) as ReactiveProperty<string>;
}
